import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { requester } from 'api/pesabackend';
import { useHierarchicalChartOfAccounts, useCreateAccount } from 'api/hooks/accounting';
import LedgerTransactionsTable from './ledgerTransactionsTable';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_DEPTH = 4;
const COLSPAN = 6;
const ACCOUNT_TYPE_ORDER = ['asset', 'liability', 'equity', 'income', 'expense'];

// Static labels for each type — badge colors are computed from useTheme inside components.
const ACCOUNT_TYPE_CONFIG = {
  asset:     { label: 'Assets',      normalBalance: 'debit'  },
  liability: { label: 'Liabilities', normalBalance: 'credit' },
  equity:    { label: 'Equity',      normalBalance: 'credit' },
  income:    { label: 'Income',      normalBalance: 'credit' },
  expense:   { label: 'Expenses',    normalBalance: 'debit'  },
};

// Badge colors: light vs dark palette for each account type.
const TYPE_BADGE_LIGHT = {
  asset:     { bg: '#EFF6FF', color: '#1D4ED8' },
  liability: { bg: '#FFFBEB', color: '#92400E' },
  equity:    { bg: '#F5F3FF', color: '#6D28D9' },
  income:    { bg: '#ECFDF5', color: '#065F46' },
  expense:   { bg: '#FFF1F2', color: '#BE123C' },
};

const TYPE_BADGE_DARK = {
  asset:     { bg: 'rgba(29,78,216,0.18)',   color: '#93C5FD' },
  liability: { bg: 'rgba(180,100,0,0.18)',   color: '#FCD34D' },
  equity:    { bg: 'rgba(109,40,217,0.18)',  color: '#C4B5FD' },
  income:    { bg: 'rgba(6,120,70,0.18)',    color: '#6EE7B7' },
  expense:   { bg: 'rgba(190,18,60,0.18)',   color: '#FCA5A5' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function inferAccountType(account) {
  if (account?.account_type) return account.account_type;
  const cat = (account?.related_accounting_category?.accounting_category_name ?? '').toLowerCase();
  if (cat.includes('asset') || cat.includes('cash') || cat.includes('bank')) return 'asset';
  if (cat.includes('liabilit') || cat.includes('payable')) return 'liability';
  if (cat.includes('equity') || cat.includes('capital')) return 'equity';
  if (cat.includes('income') || cat.includes('revenue') || cat.includes('sales')) return 'income';
  if (cat.includes('expense') || cat.includes('cost') || cat.includes('cogs')) return 'expense';
  return 'asset';
}

function formatAmount(value) {
  const n = parseFloat(value);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-UG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function balanceColor(accountType, rawValue) {
  const n = parseFloat(rawValue);
  if (!n || isNaN(n)) return null; // null = use muted color from theme
  const cfg = ACCOUNT_TYPE_CONFIG[accountType];
  const side = cfg
    ? (n < 0 ? (cfg.normalBalance === 'debit' ? 'credit' : 'debit') : cfg.normalBalance)
    : 'debit';
  return side === 'debit' ? '#3B82F6' : '#F87171';
}

function sortByCode(a, b) {
  return (a.ledger_code ?? a.code ?? '').localeCompare(
    b.ledger_code ?? b.code ?? '',
    undefined,
    { numeric: true }
  );
}

// Immutably apply updater(node) to the tree node matching id.
function updateNode(nodes, id, updater) {
  return nodes.map((n) => {
    if (n.id === id) return updater(n);
    if (n.children && n.children.length) {
      return { ...n, children: updateNode(n.children, id, updater) };
    }
    return n;
  });
}

// Depth-first flatten of all nodes (used for search + deriving categories).
function flattenTree(nodes, acc = []) {
  for (const n of nodes) {
    acc.push(n);
    if (n.children && n.children.length) flattenTree(n.children, acc);
  }
  return acc;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const pal = isDark ? TYPE_BADGE_DARK : TYPE_BADGE_LIGHT;
  const meta = pal[type];
  const label = ACCOUNT_TYPE_CONFIG[type]?.label;
  if (!meta || !label) return null;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.9,
        py: 0.25,
        borderRadius: '4px',
        backgroundColor: meta.bg,
        color: meta.color,
        fontWeight: 700,
        fontSize: '10px',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
        border: `1px solid ${meta.color}44`,
      }}
    >
      {label}
    </Box>
  );
}

function StatusBadge({ isActive }) {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const activeBg    = isDark ? 'rgba(29,158,117,0.18)' : '#EBF5FF';
  const activeColor = isDark ? '#6EE7B7' : '#1D4ED8';
  const inactiveBg    = isDark ? 'rgba(190,18,60,0.18)' : '#FFEBEB';
  const inactiveColor = isDark ? '#FCA5A5' : '#DC2626';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.9,
        py: 0.25,
        borderRadius: '4px',
        backgroundColor: isActive ? activeBg : inactiveBg,
        color: isActive ? activeColor : inactiveColor,
        fontWeight: 700,
        fontSize: '10px',
        letterSpacing: '0.04em',
        border: `1px solid ${isActive ? activeColor : inactiveColor}44`,
      }}
    >
      {isActive ? 'ACTIVE' : 'INACTIVE'}
    </Box>
  );
}

function GroupHeader({ type, count, colors }) {
  const { label } = ACCOUNT_TYPE_CONFIG[type] ?? { label: type };
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const pal = isDark ? TYPE_BADGE_DARK : TYPE_BADGE_LIGHT;
  const meta = pal[type] ?? { bg: colors.headerBg, color: colors.accent };

  return (
    <TableRow sx={{ backgroundColor: colors.headerBg, '&:hover': { backgroundColor: colors.headerBg } }}>
      <TableCell
        colSpan={COLSPAN}
        sx={{ py: 0.75, borderBottom: `1px solid ${colors.borderColor}` }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1,
              py: 0.3,
              borderRadius: '4px',
              backgroundColor: meta.bg,
              color: meta.color,
              fontWeight: 700,
              fontSize: '11px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              border: `1px solid ${meta.color}44`,
            }}
          >
            {label}
          </Box>
          <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 500 }}>
            {count} {count === 1 ? 'account' : 'accounts'}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
}

function AccountRow({
  account,
  indentLevel,
  isExpanded,
  hasChildren,
  onToggle,
  onViewEntries,
  onAddChild,
  accountType,
  colors,
}) {
  const balance = account.balance ?? account.ledger_balance ?? null;
  const bColor  = balance != null ? balanceColor(accountType, balance) : null;
  const depth   = account.depth ?? indentLevel;
  const canAddChild = depth < MAX_DEPTH;

  return (
    <TableRow
      sx={{
        '&:hover': { backgroundColor: colors.hoverBg },
        cursor: 'default',
      }}
    >
      {/* Code */}
      <TableCell
        sx={{
          fontFamily: 'monospace',
          fontSize: '12px',
          color: colors.textMuted,
          width: 100,
          pl: 2,
          borderBottom: `1px solid ${colors.cellBorder}`,
        }}
      >
        {account.ledger_code ?? account.code ?? '—'}
      </TableCell>

      {/* Account Name + expand chevron */}
      <TableCell sx={{ minWidth: 220, borderBottom: `1px solid ${colors.cellBorder}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: `${indentLevel * 20}px` }}>
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={onToggle}
              sx={{ p: 0.25, color: colors.accent }}
            >
              {isExpanded
                ? <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                : <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          ) : (
            <Box sx={{ width: 26 }} />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: indentLevel === 0 ? 600 : 400,
              fontSize: indentLevel === 0 ? '13px' : '12px',
              color: colors.textPrimary,
            }}
          >
            {account.ledger_name ?? account.name ?? '—'}
          </Typography>
        </Box>
      </TableCell>

      {/* Type badge — only at root level */}
      <TableCell sx={{ width: 110, borderBottom: `1px solid ${colors.cellBorder}` }}>
        {indentLevel === 0 && <TypeBadge type={accountType} />}
      </TableCell>

      {/* Status */}
      <TableCell sx={{ width: 90, borderBottom: `1px solid ${colors.cellBorder}` }}>
        <StatusBadge isActive={account.is_active} />
      </TableCell>

      {/* Balance */}
      <TableCell sx={{ width: 150, textAlign: 'right', borderBottom: `1px solid ${colors.cellBorder}` }}>
        {balance != null ? (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '12px',
              fontWeight: 500,
              color: bColor ?? colors.textMuted,
              textAlign: 'right',
            }}
          >
            {formatAmount(balance)}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: colors.textMuted, textAlign: 'right', fontSize: '12px' }}>
            —
          </Typography>
        )}
      </TableCell>

      {/* Actions */}
      <TableCell sx={{ width: 170, textAlign: 'center', whiteSpace: 'nowrap', borderBottom: `1px solid ${colors.cellBorder}` }}>
        {account.is_final_level && (
          <Button
            size="small"
            variant="text"
            onClick={() => onViewEntries(account)}
            sx={{
              fontSize: '11px',
              textTransform: 'none',
              color: colors.accent,
              '&:hover': { backgroundColor: colors.hoverBg },
            }}
          >
            View Entries
          </Button>
        )}
        {onAddChild && (
          <Tooltip title={canAddChild ? 'Add sub-account' : `Max depth (${MAX_DEPTH}) reached`}>
            <span>
              <IconButton
                size="small"
                disabled={!canAddChild}
                onClick={() => onAddChild(account)}
                sx={{ p: 0.25, color: canAddChild ? colors.accent : colors.textMuted }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}

function AccountTreeRow({
  account,
  accountType,
  indentLevel,
  expanded,
  loadingIds,
  errorIds,
  onToggle,
  onViewEntries,
  onAddChild,
  colors,
}) {
  const id = account.id;
  const isExpanded = expanded.has(id);
  const children = account.children ?? [];
  const childrenCount = account.children_count ?? children.length;
  const hasChildren = childrenCount > 0;
  const isLoadingChildren = loadingIds.has(id);
  const childError = errorIds[id];
  const sortedChildren = [...children].sort(sortByCode);

  return (
    <>
      <AccountRow
        account={account}
        indentLevel={indentLevel}
        isExpanded={isExpanded}
        hasChildren={hasChildren}
        onToggle={() => onToggle(account)}
        onViewEntries={onViewEntries}
        onAddChild={onAddChild}
        accountType={accountType}
        colors={colors}
      />

      {isExpanded && isLoadingChildren && (
        <TableRow>
          <TableCell
            colSpan={COLSPAN}
            sx={{
              py: 1.5,
              pl: `${(indentLevel + 1) * 20 + 40}px`,
              borderBottom: `1px solid ${colors.cellBorder}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.textMuted }}>
              <CircularProgress size={13} sx={{ color: colors.accent }} />
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                Loading sub-accounts…
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      )}

      {isExpanded && childError && (
        <TableRow>
          <TableCell
            colSpan={COLSPAN}
            sx={{ py: 1, pl: `${(indentLevel + 1) * 20 + 40}px`, borderBottom: `1px solid ${colors.cellBorder}` }}
          >
            <Typography variant="caption" sx={{ color: '#F87171' }}>
              {childError}
            </Typography>
          </TableCell>
        </TableRow>
      )}

      {isExpanded &&
        sortedChildren.map((child) => (
          <AccountTreeRow
            key={child.id}
            account={child}
            accountType={inferAccountType(child) || accountType}
            indentLevel={indentLevel + 1}
            expanded={expanded}
            loadingIds={loadingIds}
            errorIds={errorIds}
            onToggle={onToggle}
            onViewEntries={onViewEntries}
            onAddChild={onAddChild}
            colors={colors}
          />
        ))}
    </>
  );
}

// ─── Create-account dialog ────────────────────────────────────────────────────

function CreateAccountDialog({ open, onClose, parent, categories, onSubmit, isCreating }) {
  const isChild = Boolean(parent);
  const [ledgerName, setLedgerName] = useState('');
  const [ledgerCode, setLedgerCode] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (open) {
      setLedgerName('');
      setLedgerCode('');
      setCategoryId('');
      setFormError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    setFormError('');
    if (!ledgerName.trim() || !ledgerCode.trim()) {
      setFormError('Account name and code are required.');
      return;
    }
    if (!isChild && !categoryId) {
      setFormError('Please select an accounting category.');
      return;
    }
    const result = await onSubmit({
      ledgerName: ledgerName.trim(),
      ledgerCode: ledgerCode.trim(),
      categoryId: isChild ? undefined : categoryId,
    });
    if (result && !result.ok) {
      setFormError(result.error || 'Failed to create account.');
    }
  };

  const parentType = parent ? inferAccountType(parent) : null;
  const parentTypeCfg = parentType ? ACCOUNT_TYPE_CONFIG[parentType] : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontSize: '15px', fontWeight: 700 }}>
        {isChild ? 'Add Sub-account' : 'Add Root Account'}
      </DialogTitle>
      <DialogContent>
        {isChild && (
          <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>
            Under <b>{parent.ledger_name}</b> ({parent.ledger_code}).
            {parentTypeCfg && <> Inherits the <b>{parentTypeCfg.label}</b> category.</>}
          </Typography>
        )}
        <TextField
          label="Account Name"
          value={ledgerName}
          onChange={(e) => setLedgerName(e.target.value)}
          fullWidth size="small" sx={{ mt: 1 }}
        />
        <TextField
          label="Account Code"
          value={ledgerCode}
          onChange={(e) => setLedgerCode(e.target.value)}
          fullWidth size="small" sx={{ mt: 2 }}
        />
        {!isChild && (
          <TextField
            label="Accounting Category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            select fullWidth size="small" sx={{ mt: 2 }}
          >
            {categories.length === 0 && (
              <MenuItem value="" disabled>No categories available</MenuItem>
            )}
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.accounting_category_name}
                {c.accounting_category_code ? ` (${c.accounting_category_code})` : ''}
              </MenuItem>
            ))}
          </TextField>
        )}
        {formError && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#F87171' }}>
            {formError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small" disabled={isCreating}>Cancel</Button>
        <Button onClick={handleSubmit} size="small" variant="contained" disabled={isCreating}>
          {isCreating ? <CircularProgress size={16} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ChartOfAccountsTreeTable() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Derive all theme-aware color tokens once.
  const colors = useMemo(() => ({
    bodyBg:      theme.palette.background.alt,
    textPrimary: theme.palette.neutral.dark,
    textMuted:   theme.palette.neutral.main,
    accent:      theme.palette.secondary.main,
    borderColor: isDark ? 'rgba(29,158,117,0.18)' : 'rgba(29,158,117,0.20)',
    headerBg:    isDark ? 'rgba(29,158,117,0.09)' : 'rgba(29,158,117,0.07)',
    cellBorder:  isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)',
    altRowBg:    isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
    hoverBg:     isDark ? 'rgba(29,158,117,0.08)' : 'rgba(29,158,117,0.05)',
    inputBorder: isDark ? 'rgba(29,158,117,0.22)' : 'rgba(29,158,117,0.25)',
  }), [theme, isDark]);

  const business_branch_id = useSelector((state) => state.authReducer.business_branch_id);
  const { accountsList, isLoadingAccounts } = useHierarchicalChartOfAccounts(business_branch_id);
  const { createAccount, isCreating } = useCreateAccount();

  // The tree is the single source of truth.
  const [tree, setTree] = useState([]);
  const [loadedIds, setLoadedIds]   = useState(new Set());
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [errorIds, setErrorIds]     = useState({});

  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch]         = useState('');
  const [filterType, setFilterType] = useState('all');
  const [drawerAccount, setDrawerAccount] = useState(null);
  const [createParent, setCreateParent]   = useState(undefined);
  const [createOpen, setCreateOpen]       = useState(false);

  useEffect(() => { setTree(accountsList ?? []); }, [accountsList]);

  const categories = useMemo(() => {
    const byId = new Map();
    for (const n of flattenTree(tree)) {
      const c = n.related_accounting_category;
      if (c && c.id != null && !byId.has(c.id)) byId.set(c.id, c);
    }
    return [...byId.values()];
  }, [tree]);

  const relatedBusiness = useMemo(() => tree?.[0]?.related_business ?? null, [tree]);

  const groups = useMemo(() => {
    const roots   = [...tree].sort(sortByCode);
    const filtered = filterType === 'all' ? roots : roots.filter((a) => inferAccountType(a) === filterType);
    return ACCOUNT_TYPE_ORDER
      .map((type) => ({ type, rows: filtered.filter((a) => inferAccountType(a) === type) }))
      .filter((g) => g.rows.length > 0);
  }, [tree, filterType]);

  const trimmedSearch = search.trim().toLowerCase();
  const isSearching   = trimmedSearch.length > 0;

  const filteredFlat = useMemo(() => {
    if (!isSearching) return [];
    return flattenTree(tree)
      .filter((a) => {
        const type = inferAccountType(a);
        if (filterType !== 'all' && type !== filterType) return false;
        const code = (a.ledger_code ?? a.code ?? '').toLowerCase();
        const name = (a.ledger_name ?? a.name ?? '').toLowerCase();
        return code.includes(trimmedSearch) || name.includes(trimmedSearch);
      })
      .sort(sortByCode);
  }, [isSearching, trimmedSearch, tree, filterType]);

  const loadChildren = async (account) => {
    const id = account.id;
    setLoadingIds((prev) => new Set(prev).add(id));
    try {
      const response = await requester.get(`/accounting/chart_of_accounts/${id}/children`);
      const children = response.data.data?.children ?? response.data.data ?? [];
      setTree((prev) => updateNode(prev, id, (n) => ({ ...n, children, children_count: children.length })));
      setLoadedIds((prev) => new Set(prev).add(id));
      setErrorIds((prev) => { const next = { ...prev }; delete next[id]; return next; });
    } catch {
      setErrorIds((prev) => ({ ...prev, [id]: 'Failed to load sub-accounts.' }));
    } finally {
      setLoadingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const handleToggle = (account) => {
    const id = account.id;
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    const childrenCount = account.children_count ?? account.children?.length ?? 0;
    const embedded      = account.children?.length ?? 0;
    if (childrenCount > 0 && embedded === 0 && !loadedIds.has(id) && !loadingIds.has(id)) {
      loadChildren(account);
    }
  };

  const handleViewEntries  = (account) => setDrawerAccount(account);

  const openCreateRoot  = () => { setCreateParent(null);   setCreateOpen(true); };
  const openCreateChild = (parent) => { setCreateParent(parent); setCreateOpen(true); };
  const closeCreate     = () => { setCreateOpen(false); setTimeout(() => setCreateParent(undefined), 200); };

  const handleCreateSubmit = async ({ ledgerName, ledgerCode, categoryId }) => {
    const isChild = Boolean(createParent);
    const payload = {
      related_business:        relatedBusiness ?? createParent?.related_business,
      related_business_branch: business_branch_id,
      ledger_name:  ledgerName,
      ledger_code:  ledgerCode,
      ...(isChild
        ? { parent_id: createParent.id }
        : { related_accounting_category: categoryId }),
    };

    const result = await createAccount(payload);
    if (!result.ok) return result;

    const created = { children: [], children_count: 0, ...result.data };

    if (isChild) {
      const parentId     = createParent.id;
      const parentLoaded = loadedIds.has(parentId) || (createParent.children?.length ?? 0) > 0;
      setTree((prev) =>
        updateNode(prev, parentId, (n) => ({
          ...n,
          children_count: (n.children_count ?? n.children?.length ?? 0) + 1,
          children: parentLoaded ? [...(n.children ?? []), created] : (n.children ?? []),
        }))
      );
      if (parentLoaded) setExpanded((prev) => new Set(prev).add(parentId));
    } else {
      setTree((prev) => [...prev, created]);
    }

    closeCreate();
    return result;
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const headCellSx = {
    fontWeight: 700,
    fontSize: '11px',
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: colors.accent,
    backgroundColor: colors.headerBg,
    borderBottom: `1px solid ${colors.borderColor}`,
    whiteSpace: 'nowrap',
    py: 1.25,
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: `1px solid ${colors.inputBorder}`,
            borderRadius: '8px',
            px: 1,
            flex: 1,
            minWidth: 200,
            maxWidth: 340,
            height: 36,
            backgroundColor: colors.bodyBg,
          }}
        >
          <SearchIcon sx={{ color: colors.textMuted, fontSize: 17, mr: 0.5 }} />
          <InputBase
            placeholder="Search by name or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: '13px', flex: 1, color: colors.textPrimary }}
          />
        </Box>

        <Select
          size="small"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          sx={{
            fontSize: '13px',
            height: 36,
            minWidth: 150,
            color: colors.textPrimary,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.inputBorder },
          }}
        >
          <MenuItem value="all">All types</MenuItem>
          {ACCOUNT_TYPE_ORDER.map((t) => (
            <MenuItem key={t} value={t}>{ACCOUNT_TYPE_CONFIG[t].label}</MenuItem>
          ))}
        </Select>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreateRoot}
          sx={{ textTransform: 'none', height: 36, fontWeight: 600 }}
        >
          Add Root Account
        </Button>
      </Box>

      {/* Table */}
      <Box
        sx={{
          backgroundColor: colors.bodyBg,
          border: `1px solid ${colors.borderColor}`,
          borderRadius: '12px',
          overflowX: 'auto',
        }}
      >
        <TableContainer>
          <Table size="small" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: 100, pl: 2 }}>Code</TableCell>
                <TableCell sx={headCellSx}>Account Name</TableCell>
                <TableCell sx={{ ...headCellSx, width: 110 }}>Type</TableCell>
                <TableCell sx={{ ...headCellSx, width: 90 }}>Status</TableCell>
                <TableCell sx={{ ...headCellSx, width: 150, textAlign: 'right' }}>Balance</TableCell>
                <TableCell sx={{ ...headCellSx, width: 170, textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoadingAccounts ? (
                <TableRow>
                  <TableCell colSpan={COLSPAN} sx={{ textAlign: 'center', py: 5, border: 'none' }}>
                    <CircularProgress size={24} sx={{ color: colors.accent }} />
                  </TableCell>
                </TableRow>
              ) : isSearching ? (
                filteredFlat.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLSPAN} sx={{ textAlign: 'center', py: 5, color: colors.textMuted, fontSize: '13px', border: 'none' }}>
                      No accounts matching "{search.trim()}"
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFlat.map((a) => (
                    <AccountRow
                      key={a.id}
                      account={a}
                      indentLevel={0}
                      isExpanded={false}
                      hasChildren={false}
                      onToggle={() => undefined}
                      onViewEntries={handleViewEntries}
                      onAddChild={openCreateChild}
                      accountType={inferAccountType(a)}
                      colors={colors}
                    />
                  ))
                )
              ) : groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLSPAN} sx={{ textAlign: 'center', py: 5, color: colors.textMuted, fontSize: '13px', border: 'none' }}>
                    No accounts found.
                  </TableCell>
                </TableRow>
              ) : (
                groups.map(({ type, rows }) => (
                  <Fragment key={`group-${type}`}>
                    <GroupHeader type={type} count={rows.length} colors={colors} />
                    {rows.map((account) => (
                      <AccountTreeRow
                        key={account.id}
                        account={account}
                        accountType={type}
                        indentLevel={0}
                        expanded={expanded}
                        loadingIds={loadingIds}
                        errorIds={errorIds}
                        onToggle={handleToggle}
                        onViewEntries={handleViewEntries}
                        onAddChild={openCreateChild}
                        colors={colors}
                      />
                    ))}
                  </Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Create-account dialog */}
      <CreateAccountDialog
        open={createOpen}
        parent={createParent || null}
        categories={categories}
        onClose={closeCreate}
        onSubmit={handleCreateSubmit}
        isCreating={isCreating}
      />

      {/* Ledger entries drawer */}
      <Drawer
        anchor="right"
        open={Boolean(drawerAccount)}
        onClose={() => setDrawerAccount(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 620 },
            p: 2,
            backgroundColor: colors.bodyBg,
          },
        }}
      >
        {drawerAccount && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: colors.textPrimary }}>
                  {drawerAccount.ledger_name ?? drawerAccount.name}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: 'monospace' }}>
                  {drawerAccount.ledger_code ?? drawerAccount.code}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDrawerAccount(null)}
                sx={{ borderColor: colors.borderColor, color: colors.textPrimary }}
              >
                Close
              </Button>
            </Box>
            <LedgerTransactionsTable
              ledgerId={drawerAccount.id}
              ledgerName={drawerAccount.ledger_name ?? drawerAccount.name}
              embedded
            />
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
